import React, { useState, useRef } from "react";
import { Volume2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

/**
 * Slice R · "Play in my language" — TTS speaker for translated voice-note
 * transcripts. Closes the linguistic loop: the driver spoke in Hindi, the
 * rider now hears the same words in English (or whatever their UI language).
 *
 *   1. Translate transcript → user's language (skipped if already same lang)
 *   2. POST /api/ai/voice/speak → MP3 blob
 *   3. Play through a hidden <audio> element
 *
 * Both the translation and the audio blob are cached by target-lang+text so
 * replays never re-charge the LLM.
 */
const _TR_CACHE  = new Map();   // key = `${target}:::${text}` → translated
const _MP3_CACHE = new Map();   // key = `${target}:::${text}` → object URL

function guessLangShort(s) {
  const t = s || "";
  if (/[\u0900-\u097F]/.test(t)) return "hi";
  if (/[\u4E00-\u9FFF]/.test(t)) return "zh";
  if (/[\u3040-\u30FF]/.test(t)) return "ja";
  if (/[\uAC00-\uD7AF]/.test(t)) return "ko";
  if (/[\u0600-\u06FF]/.test(t)) return "ar";
  if (/[\u0400-\u04FF]/.test(t)) return "ru";
  return "en";
}

export default function SpeakChip({ text, sourceLang, className = "", testId = "speak-chip", onlyWhenTranslated = false }) {
  const { lang } = useI18n();
  const [state, setState]   = useState("idle");   // idle | loading | playing | error
  const audioRef = useRef(null);

  const clean = (text || "").trim();
  if (!clean || clean.length < 2) return null;

  const target = (lang || "en").slice(0, 2);
  const detected = (sourceLang || guessLangShort(clean)).slice(0, 2);

  // Text-message gate: only surface the button if it will actually help,
  // i.e. the source language differs from the reader's. Voice notes always
  // show it because "read the transcript" ≠ "hear the words".
  if (onlyWhenTranslated && detected === target) return null;

  const play = async () => {
    if (state === "loading") return;
    if (state === "playing") {
      audioRef.current?.pause();
      audioRef.current && (audioRef.current.currentTime = 0);
      setState("idle");
      return;
    }
    const key = `${target}:::${clean}`;
    try {
      setState("loading");

      // Step 1 — pick the words to speak. Skip translation if already in
      // the user's language.
      let toSpeak;
      if (detected === target) {
        toSpeak = clean;
      } else if (_TR_CACHE.has(key)) {
        toSpeak = _TR_CACHE.get(key);
      } else {
        const r = await api.post("/ai/translate", {
          text: clean, target_lang: target, source_lang: detected,
        });
        toSpeak = r.data?.translated || clean;
        _TR_CACHE.set(key, toSpeak);
      }

      // Step 2 — synth audio (or reuse cached blob URL).
      let url = _MP3_CACHE.get(key);
      if (!url) {
        const resp = await api.post(
          "/ai/voice/speak",
          { text: toSpeak, voice: "alloy", format: "mp3" },
          { responseType: "blob" },
        );
        const blob = resp.data instanceof Blob
          ? resp.data
          : new Blob([resp.data], { type: "audio/mp3" });
        url = URL.createObjectURL(blob);
        _MP3_CACHE.set(key, url);
      }

      // Step 3 — play. Reset state when audio finishes.
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = url;
      audioRef.current.onended = () => setState("idle");
      audioRef.current.onerror = () => setState("error");
      await audioRef.current.play();
      setState("playing");
    } catch {
      setState("error");
      setTimeout(() => setState("idle"), 2500);
    }
  };

  const label = state === "loading"
    ? "Preparing…"
    : state === "playing"
      ? "■ Stop"
      : state === "error"
        ? "Try again"
        : `Play in ${target.toUpperCase()}`;

  return (
    <button
      type="button"
      onClick={play}
      className={`inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 ${className}`}
      data-testid={testId}
      aria-label={`Play voice note in ${target.toUpperCase()}`}
    >
      {state === "loading"
        ? <Loader2 size={11} className="animate-spin" />
        : <Volume2 size={11} />}
      {label}
    </button>
  );
}
